/**
 * Content selection and layout model for the thread PDF (IMPLEMENTATION_PLAN
 * §6.1, §12). Pure: it turns `GET /tickets/:id/export` into a flat list of
 * blocks; src/lib/exportPdfRender.ts draws them with jspdf.
 *
 * The server already filters by audience. A customer PDF filters again here
 * (defence in depth): no internal note, internal message or internal
 * attachment can reach it even if the server payload were wrong.
 */
import type { MessageView } from '@/api/messagesApi'
import type { ExportAudience, TicketExport } from '@/api/ticketsApi'
import { dateTimeLabel, formatBytes, timeLabel, dayLabel, dayKey } from '@/lib/threadView'

export type PdfBlock =
  | { kind: 'title'; text: string }
  | { kind: 'subtitle'; text: string }
  | { kind: 'section'; text: string }
  | { kind: 'day'; text: string }
  | { kind: 'author'; text: string; internal: boolean }
  | { kind: 'body'; text: string; internal: boolean }
  | { kind: 'system'; text: string }
  | { kind: 'field'; label: string; value: string }
  | { kind: 'spacer' }

export interface PdfDocument {
  fileName: string
  blocks: PdfBlock[]
  /** Printed on every page. */
  footer: string
}

/** Every string the PDF prints, supplied by the caller from i18n. */
export interface PdfLabels {
  thread: string
  resolution: string
  attachments: string
  internalNote: string
  via: (channel: string) => string
  resolvedBy: string
  resolvedAt: string
  category: string
  rootCause: string
  deviation: string
  reason: string
  summary: string
  none: string
  audience: (audience: ExportAudience) => string
  auditHead: (hash: string, verified: boolean) => string
  generated: (at: string, by: string) => string
  categoryLabel: (value: string) => string
  deviationLabel: (value: string) => string
  opened: (when: string, by: string) => string
}

/** True when a message may appear in a PDF for `audience`. */
export function messageAllowed(message: MessageView, audience: ExportAudience): boolean {
  if (audience === 'internal') return true
  return message.kind !== 'internal_note' && message.visibility === 'customer'
}

function generatedByName(by: TicketExport['generatedBy']): string {
  return typeof by === 'string' ? by : by.name
}

export function buildPdfDocument(
  data: TicketExport,
  audience: ExportAudience,
  labels: PdfLabels,
  timeZone: string,
): PdfDocument {
  const { ticket } = data
  const blocks: PdfBlock[] = [
    { kind: 'title', text: `${ticket.key} · ${ticket.subject}` },
    {
      kind: 'subtitle',
      text: [ticket.customer.name, ticket.tenant?.name, ticket.product.name, ticket.reporter ? labels.opened(dateTimeLabel(ticket.createdAt, timeZone), ticket.reporter.name) : null]
        .filter(Boolean)
        .join(' · '),
    },
    { kind: 'spacer' },
    { kind: 'section', text: labels.thread },
  ]

  let day: string | null = null
  for (const m of data.messages.filter((x) => messageAllowed(x, audience))) {
    const d = dayKey(m.createdAt, timeZone)
    if (d !== day) {
      day = d
      blocks.push({ kind: 'day', text: dayLabel(m.createdAt, timeZone) })
    }
    const time = timeLabel(m.createdAt, timeZone)
    if (m.kind === 'system_event') {
      blocks.push({ kind: 'system', text: `${time}  ${m.body}` })
      continue
    }
    const internal = m.kind === 'internal_note'
    const meta = [m.author.name, m.author.subtitle, time, m.kind === 'customer_reply' && m.channel ? labels.via(m.channel) : null]
    if (internal) meta.unshift(labels.internalNote)
    blocks.push({ kind: 'author', text: meta.filter(Boolean).join(' · '), internal })
    blocks.push({ kind: 'body', text: m.body, internal })
    const files = m.attachments.filter((a) => audience === 'internal' || a.visibility === 'customer')
    if (files.length > 0) {
      blocks.push({ kind: 'system', text: files.map((a) => `${a.fileName} (${formatBytes(a.sizeBytes)})`).join(', ') })
    }
  }

  const resolution = data.resolution
  if (resolution) {
    blocks.push({ kind: 'spacer' }, { kind: 'section', text: labels.resolution })
    blocks.push(
      { kind: 'field', label: labels.resolvedBy, value: resolution.resolvedBy.name },
      { kind: 'field', label: labels.resolvedAt, value: dateTimeLabel(resolution.resolvedAt, resolution.timeZone || timeZone) },
      { kind: 'field', label: labels.category, value: labels.categoryLabel(resolution.category) },
    )
    if (resolution.rootCause) blocks.push({ kind: 'field', label: labels.rootCause, value: resolution.rootCause })
    blocks.push({
      kind: 'field',
      label: labels.deviation,
      value: resolution.deviationDecision ? labels.deviationLabel(resolution.deviationDecision) : labels.none,
    })
    blocks.push({ kind: 'field', label: labels.reason, value: resolution.reason })
    blocks.push({ kind: 'field', label: labels.summary, value: resolution.summary })
  }

  const attachments = data.attachments.filter((a) => audience === 'internal' || (a.visibility ?? 'customer') === 'customer')
  if (attachments.length > 0) {
    blocks.push({ kind: 'spacer' }, { kind: 'section', text: labels.attachments })
    for (const a of attachments) blocks.push({ kind: 'field', label: a.fileName, value: formatBytes(a.sizeBytes) })
  }

  const footer = [
    labels.audience(audience),
    labels.auditHead(data.audit.headHash ?? '—', Boolean(data.audit.valid ?? data.audit.verified)),
    labels.generated(dateTimeLabel(data.generatedAt, timeZone), generatedByName(data.generatedBy)),
  ].join(' · ')

  return { fileName: `${ticket.key}-${audience}.pdf`, blocks, footer }
}
